// src/components/HRComponents/NotificationForm.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from "react-toastify";

import api from "../../common/apiClient"; // Global apiClient
import SummaryApi from "../../common/SummaryApi";
import SelectDropdown from '../../customStyles/SelectDropdown'; // Reusable customized dropdown

const TARGET_TYPE_OPTIONS = [
  { label: "All Users", value: "all" },
  { label: "Single User", value: "user" },
  // { label: "Organization", value: "organization" },
  { label: "Department", value: "department" },
  { label: "Division", value: "division" },
];

const NotificationForm = () => {
  const navigate = useNavigate();

  // Core options fetched dynamically on mount [1]
  const [users, setUsers] = useState([]);
  // const [organizations, setOrganizations] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [departments, setDepartments] = useState([]); // Fetched dynamically on choosing division

  const [isSaving, setIsSaving] = useState(false);
  const [selectedDivisionId, setSelectedDivisionId] = useState(""); // Helper for nested department filtering [1]

  const [form, setForm] = useState({
    title: "",
    message: "",
    targetType: "all",
    targetId: "",
  });

  // Fetch users, organizations, and divisions inline on component mount [1]
  useEffect(() => {
    // 1. Fetch active users
    api.get(SummaryApi.getAllEmployees?.url || "/api/hr/users")
      .then((res) => { if (res.data.success) setUsers(res.data.data || []); })
      .catch(() => {});

    // 2. Fetch organizations
    // api.get(SummaryApi.getOrganizations?.url || "/api/hr/organizations")
    //   .then((res) => { if (res.data.success) setOrganizations(res.data.data || []); })
    //   .catch(() => {});

    // 3. Fetch divisions
    api.get(SummaryApi.getAllDivisions?.url || "/api/hr/divisions")
      .then((res) => { if (res.data.success) setDivisions(res.data.data || []); })
      .catch(() => {});
  }, []);

  // Dynamically fetch departments when a division is selected (Enforces clean schema relations) [1]
  const handleDivisionChangeForDepartment = async (divisionId) => {
    setSelectedDivisionId(divisionId);
    setForm((prev) => ({ ...prev, targetId: "" })); // Reset target selection
    setDepartments([]);

    if (!divisionId) return;

    try {
      // Backend filters departments based on divisionId query param
      const res = await api.get(SummaryApi.getAllDepartments?.url || "/api/hr/departments", { params: { divisionId } });
      if (res.data.success) {
        setDepartments(res.data.data || []);
      }
    } catch {
      setDepartments([]);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Dispatch the formatted payload dynamically to the respective backend controller endpoint [1]
  const sendNotification = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.message.trim()) return toast.error("Message is required");
    if (form.targetType !== "all" && !form.targetId) return toast.error("Please select a target");

    setIsSaving(true);
    try {
      let url = "";
      let payload = { title: form.title, message: form.message };

      // Dynamically select target URL matching your backend router paths [1]
      if (form.targetType === "all") {
        url = `${SummaryApi.sendPushNotification.url}-all`;
      } else if (form.targetType === "user") {
        url = `${SummaryApi.sendPushNotification.url}-user`;
        payload.userId = form.targetId;
      } 
      // else if (form.targetType === "organization") {
      //   url = `${SummaryApi.sendPushNotification.url}-organization`;
      //   payload.organizationId = form.targetId;
      // } 
      else if (form.targetType === "department") {
        url = `${SummaryApi.sendPushNotification.url}-department`;
        payload.departmentId = form.targetId;
      } else if (form.targetType === "division") {
        url = `${SummaryApi.sendPushNotification.url}-division`;
        payload.divisionId = form.targetId;
      }

      const res = await api.post(url, payload);

      if (res.data.success) {
        toast.success("Notification sent successfully");
        setForm({
          title: "",
          message: "",
          targetType: "all",
          targetId: "",
        });
        setSelectedDivisionId("");
        setDepartments([]);
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Notification failed to send");
    } finally {
      setIsSaving(false);
    }
  };

  // Formats employee option labels cleanly
  const formattedUsers = users.map((u) => ({
    _id: u._id,
    labelName: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
  }));

  return (
    <div className="p-4">
      {/* Header section with back navigation and title */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-1.5 sm:p-2 transition-colors"
        >
          <FaArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
        <h1 className="text-lg sm:text-2xl font-bold text-gray-900">New Push Notification</h1>
      </div>

      {/* Styled Form Container matching your system guidelines */}
      <form onSubmit={sendNotification} className="bg-white rounded-xl border p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Notification Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="e.g. Check-in Reminder, Team Meeting"
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Message Body</label>
            <textarea
              rows={4}
              value={form.message}
              onChange={(e) => updateField('message', e.target.value)}
              placeholder="Type your push notification alert message here..."
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none text-sm"
            />
          </div>

          <SelectDropdown
            label="Send Target"
            options={TARGET_TYPE_OPTIONS}
            value={form.targetType}
            onChange={(v) => { updateField('targetType', v); updateField('targetId', ''); setSelectedDivisionId(''); }}
            valueKey="value" labelKey="label"
          />

          {/* DYNAMIC SUB-SECTIONS: Displays based on chosen targetType [1] */}
          
          {/* 1. Target: Single User */}
          {form.targetType === "user" && (
            <SelectDropdown
              label="Select Target Employee"
              options={formattedUsers}
              value={form.targetId}
              onChange={(v) => updateField('targetId', v)}
              valueKey="_id" labelKey="labelName" searchable
            />
          )}

          {/* 2. Target: Specific Organization */}
          {/* {form.targetType === "organization" && (
            <SelectDropdown
              label="Select Target Organization"
              options={organizations}
              value={form.targetId}
              onChange={(v) => updateField('targetId', v)}
              valueKey="_id" labelKey="name" searchable
            />
          )} */}

          {/* 3. Target: Specific Division */}
          {form.targetType === "division" && (
            <SelectDropdown
              label="Select Target Division"
              options={divisions}
              value={form.targetId}
              onChange={(v) => updateField('targetId', v)}
              valueKey="_id" labelKey="name" searchable
            />
          )}

          {/* 4. Target: Specific Department (Requires choosing Division first to sync with backend controller) [1] */}
          {form.targetType === "department" && (
            <>
              <SelectDropdown
                label="Step 1: Choose Division"
                options={divisions}
                value={selectedDivisionId}
                onChange={handleDivisionChangeForDepartment}
                valueKey="_id" labelKey="name" searchable
              />
              <SelectDropdown
                label="Step 2: Select Target Department"
                options={departments}
                value={form.targetId}
                onChange={(v) => updateField('targetId', v)}
                valueKey="_id" labelKey="name" searchable
                disabled={!selectedDivisionId}
              />
            </>
          )}
        </div>

        {/* Form Action Buttons */}
        <div className="flex justify-end gap-3 pt-2 border-t">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm disabled:opacity-50"
          >
            {isSaving ? "Sending..." : "Send Notification"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NotificationForm;